import { useState } from 'react';
import { ProfileCardGrid, ProfileGridItem } from '@/components/common/ProfileCardGrid';
import { ProfileLayout } from '@/components/common/ProfileLayout';
import { SectionEditButton } from '@/components/common/SectionEditButton';
import { ROUTES } from '@/lib/constants/routes';
import type { StartupProfileData } from '../api/startup-profile.api';
import { useStartupProfilePdf } from '../hooks/useStartupProfilePdf';
import { StartupProfileHero } from './startup/StartupProfileHero';
import { StartupSectionEditor } from './startup/StartupSectionEditor';
import { STARTUP_SECTION_VIEWS } from './startup/StartupSectionContent';
import {
  STARTUP_SECTION_ORDER,
  STARTUP_SECTIONS,
  type StartupSectionId,
} from './startup/startup-sections';

interface StartupProfileSectionsProps {
  data: StartupProfileData;
  readOnly: boolean;
  entityId: string;
  /**
   * Whether to offer the "Export PDF" action. Defaults to true, which is what
   * every member route wants; the admin's read-only view of someone else's
   * profile passes false, because the PDF route is member-scoped
   * (`assertOwnEntityScope`) and would answer an admin with a 403. Offering an
   * action the caller cannot perform is the dead-UI rule from
   * ui-guidelines.md §12. The share preview route has the same member-only
   * scope, so this also decides whether "Preview as investor" is offered.
   */
  canExport?: boolean;
}

/**
 * The startup profile module's `Sections` component (ADR FE-1 registry): the
 * header plus one card per section, each edited in its own side panel
 * (MYN-217, profile-redesign FR-164 to FR-173). Read-only viewers - members
 * without edit permission and the admin console - get the same page with no
 * Edit actions or completion prompts (FR-169).
 */
export function StartupProfileSections({
  data,
  readOnly,
  entityId,
  canExport = true,
}: StartupProfileSectionsProps) {
  const [openSection, setOpenSection] = useState<StartupSectionId | null>(null);
  const exportPdf = useStartupProfilePdf();

  const hero = (
    <StartupProfileHero
      data={data}
      readOnly={readOnly}
      canExport={canExport}
      previewTo={canExport ? ROUTES.STARTUP_PROFILE_PREVIEW : undefined}
      isExporting={exportPdf.isPending}
      onExport={() => exportPdf.mutate({ entityId, companyName: data.companyName })}
      onOpenSection={setOpenSection}
    />
  );

  return (
    <ProfileLayout hero={hero}>
      <ProfileCardGrid>
        {STARTUP_SECTION_ORDER.map((id) => (
          <ProfileGridItem key={id} span={STARTUP_SECTIONS[id].span}>
            {STARTUP_SECTION_VIEWS[id]({
              data,
              action: readOnly ? undefined : (
                <SectionEditButton
                  section={STARTUP_SECTIONS[id].title}
                  onClick={() => setOpenSection(id)}
                />
              ),
              onAdd: readOnly ? undefined : () => setOpenSection(id),
            })}
          </ProfileGridItem>
        ))}
      </ProfileCardGrid>
      {!readOnly && (
        <StartupSectionEditor
          data={data}
          entityId={entityId}
          section={openSection}
          onClose={() => setOpenSection(null)}
        />
      )}
    </ProfileLayout>
  );
}
